import {
  ChatAppliedRouteStop,
  ChatRouteStopCandidate,
  ChatRouteStopPickAction,
  ChatRouteStopsResult,
} from "@mapmycustomers/shared/types/chat/ChatContent";
import { FieldAgentAcceptedProposal } from "@mapmycustomers/shared/types/chat/FieldAgentChat";
import { isRecord } from "@mapmycustomers/shared/util/chat/chatWireGuards";

// Every record the rep ticked on an add-to-route picker.
const PICKS_KEY = "picks";

type RouteStopPickKey = Pick<ChatRouteStopCandidate, "entityId" | "entityType">;

export const isSameCandidate = (left: RouteStopPickKey, right: RouteStopPickKey): boolean =>
  left.entityType === right.entityType && left.entityId === right.entityId;

export const routeStopPickDraft = (...picks: RouteStopPickKey[]) => ({
  [PICKS_KEY]: picks.map(({ entityId, entityType }) => ({ entityId, entityType })),
});

// Resolved against the offered records rather than trusted: a draft restored from sessionStorage, or
// one naming a record the list no longer holds, must never reach the wire as a pick.
export const readRouteStopPicks = (
  action: ChatRouteStopPickAction,
  draft: Record<string, unknown> | undefined
): ChatRouteStopCandidate[] => {
  const picks = draft?.[PICKS_KEY];
  if (!Array.isArray(picks)) {
    return [];
  }
  return action.candidates.filter((candidate) =>
    picks.some((pick) => isRecord(pick) && isSameCandidate(candidate, pick as RouteStopPickKey))
  );
};

// The records an add-to-route card says it adds, or once applied added: the body names them and the
// title counts them, both from here, so the title never claims a count the body does not. Applied,
// what the platform reports it added wins over the ticks, which can be out of date by then. An
// empty report is no report: the platform refuses an add where nothing fits, so an empty list only
// means the parser dropped every entry, and "nothing was added" would send the rep to add them
// again. With no report a lone tick is still what was added. Several, or none left to read, make
// no claim (undefined): a server without the multi-select pick adds only the first and reports
// nothing.
export const readClaimedRouteStops = (
  action: ChatRouteStopPickAction,
  draft: Record<string, unknown> | undefined,
  result: ChatRouteStopsResult | undefined,
  applied: boolean
): ChatAppliedRouteStop[] | undefined => {
  const ticked = readRouteStopPicks(action, draft);
  if (!applied) {
    return ticked;
  }
  const reported = result?.appliedStops.length ? result.appliedStops : undefined;
  return reported ?? (ticked.length === 1 ? ticked : undefined);
};

// Ticks the record on, or off when it already is.
export const toggleRouteStopPick = (
  action: ChatRouteStopPickAction,
  draft: Record<string, unknown> | undefined,
  candidate: RouteStopPickKey
) => {
  const picks = readRouteStopPicks(action, draft);
  return picks.some((pick) => isSameCandidate(pick, candidate))
    ? routeStopPickDraft(...picks.filter((pick) => !isSameCandidate(pick, candidate)))
    : routeStopPickDraft(...picks, candidate);
};

export const toRouteStopPickAcceptedItem = (
  proposalId: FieldAgentAcceptedProposal["proposalId"],
  action: ChatRouteStopPickAction,
  draft: Record<string, unknown> | undefined
): FieldAgentAcceptedProposal => {
  const targets = readRouteStopPicks(action, draft).map(({ entityId, entityType }) => ({
    id: entityId,
    entityType,
  }));
  if (!targets.length) {
    return { proposalId };
  }
  // The first as the single target too: a server that predates the multi-select pick reads only
  // that, and a newer one takes the list when both are sent.
  return { finalTargetEntities: targets, finalTargetEntity: targets[0], proposalId };
};
